import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Mensaje from "sweetalert";
import demo from "../../icons/pregunta.png";
import { useStorePos } from "./useStorePos";
import { formatearMoneda } from "../../config/moneda";
import { calcularVentaResumen } from "../../servicios/ventas";
import { admiteCompraComoConsumidorFinal } from "../../servicios/facturacionFiscal";
import { resumirPagos, validarPagos } from "../../servicios/pagosVenta";
import PosPagosEditor from "./PosPagosEditor";
import { PERMISOS, puedeAcceder } from "../../servicios/sesionSeguridad";

const formatQty = (value, locale) =>
  new Intl.NumberFormat(locale, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);

const getLineaNombre = (linea, t) =>
  linea.ProductoNuevo || linea.TipoLinea === "Libre"
    ? linea.DescripcionManual || t("products.newProduct")
    : linea.Mercaderia?.sDescripcion || t("pos.product");

const getLineaUnidad = (linea) =>
  linea.Unidad?.sDescripcion ||
  linea.Mercaderia?.UnidadDescripcion ||
  linea.Mercaderia?.sUnidad ||
  "";

const getStockEstimado = (linea, t, locale) => {
  switch (linea.EstadoStockEstimado) {
    case "Completo":
      return t("pos.estimatedStock", { quantity: formatQty(linea.CantidadStockEstimada, locale) });
    case "Parcial":
      return t("pos.partialStock", {
        available: formatQty(linea.CantidadStockEstimada, locale),
        missing: formatQty(linea.CantidadSinStockEstimada, locale),
      });
    case "SinStock":
      return t("pos.missingStock", { quantity: formatQty(linea.CantidadSinStockEstimada, locale) });
    default:
      return t("pos.stockNotTracked");
  }
};

const PosTicket = ({ modoRapido = false }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.resolvedLanguage === "en" ? "en-US" : "es-AR";
  const [cobroAbierto, setCobroAbierto] = useState(false);
  const Cliente = useStorePos((s) => s.Cliente);
  const TicketLineas = useStorePos((s) => s.TicketLineas);
  const ConfirmandoVenta = useStorePos((s) => s.ConfirmandoVenta);
  const Pagos = useStorePos((s) => s.Pagos);
  const FormaFiscalPrincipalId = useStorePos((s) => s.FormaFiscalPrincipalId);
  const BuscandoFormasPago = useStorePos((s) => s.BuscandoFormasPago);
  const ErrorFormasPago = useStorePos((s) => s.ErrorFormasPago);
  const Observacion = useStorePos((s) => s.Observacion);
  const EstadoEnvioVenta = useStorePos((s) => s.EstadoEnvioVenta);
  const ErrorFiscal = useStorePos((s) => s.ErrorFiscal);
  const ErroresLineas = useStorePos((s) => s.ErroresLineas);
  const VentaPendiente = useStorePos((s) => s.VentaPendiente);
  const ResultadoVenta = useStorePos((s) => s.ResultadoVenta);
  const OpcionesFacturacion = useStorePos((s) => s.OpcionesFacturacion);
  const NumeradorId = useStorePos((s) => s.NumeradorId);
  const CargandoOpcionesFacturacion = useStorePos((s) => s.CargandoOpcionesFacturacion);
  const ErrorOpcionesFacturacion = useStorePos((s) => s.ErrorOpcionesFacturacion);
  const CondicionIVASeleccionada = useStorePos((s) => s.CondicionIVASeleccionada);
  const ModoCliente = useStorePos((s) => s.ModoCliente);
  const RequiereIdentificacionFiscal = useStorePos((s) => s.RequiereIdentificacionFiscal);
  const SolicitaIdentificacionFiscal = useStorePos((s) => s.SolicitaIdentificacionFiscal);
  const FacturarComoConsumidorFinal = useStorePos((s) => s.FacturarComoConsumidorFinal);
  const CondicionesIVA = useStorePos((s) => s.CondicionesIVA);
  const permiteCompraComoConsumidorFinal = ModoCliente === "cliente" &&
    admiteCompraComoConsumidorFinal(Cliente, CondicionesIVA);

  const resumen = useMemo(() => calcularVentaResumen(TicketLineas), [TicketLineas]);

  const validacionPagos = useMemo(() => validarPagos(Pagos, resumen.TotalConIva), [Pagos, resumen.TotalConIva]);
  const resumenPagos = useMemo(() => resumirPagos(Pagos), [Pagos]);
  const formaFiscalValida = Pagos.length <= 1 || FormaFiscalPrincipalId > 0;
  const puedeConfirmar =
    TicketLineas.length > 0 &&
    !ConfirmandoVenta &&
    !BuscandoFormasPago &&
    !ErrorFormasPago &&
    !CargandoOpcionesFacturacion &&
    NumeradorId > 0 &&
    !ErrorOpcionesFacturacion &&
    validacionPagos.valido &&
    formaFiscalValida &&
    !(ModoCliente === "consumidorFinal" && RequiereIdentificacionFiscal) &&
    Object.keys(ErroresLineas).length === 0;
  const motivoBloqueo = useMemo(() => {
    if (ConfirmandoVenta) return t("pos.invoiceBeingIssued");
    if (BuscandoFormasPago) return t("pos.loadingPaymentMethods");
    if (ErrorFormasPago) return ErrorFormasPago;
    if (!validacionPagos.valido) return Object.values(validacionPagos.erroresPorClave).flat()[0] || t("pos.reviewPayments");
    if (!formaFiscalValida) return t("pos.selectMainFiscalPayment");
    if (CargandoOpcionesFacturacion) return t("pos.checkingReceiptType");
    if (ErrorOpcionesFacturacion) return ErrorOpcionesFacturacion;
    if (!NumeradorId) return t("pos.selectReceiptType");
    if (ModoCliente === "consumidorFinal" && RequiereIdentificacionFiscal) {
      return t("pos.amountRequiresCustomer");
    }
    if (Object.keys(ErroresLineas).length > 0) return t("pos.reviewMarkedProducts");
    return "";
  }, [
    BuscandoFormasPago,
    CargandoOpcionesFacturacion,
    ConfirmandoVenta,
    ErrorFormasPago,
    ErrorOpcionesFacturacion,
    ErroresLineas,
    ModoCliente,
    NumeradorId,
    RequiereIdentificacionFiscal,
    formaFiscalValida,
    t,
    validacionPagos,
  ]);

  useEffect(() => {
    if (modoRapido && TicketLineas.length === 0 && !ConfirmandoVenta) {
      setCobroAbierto(false);
    }
  }, [ConfirmandoVenta, modoRapido, TicketLineas.length]);

  useEffect(() => {
    if (!modoRapido && TicketLineas.length > 0 && Pagos.length === 0) {
      useStorePos.getState().prepararCobro(resumen.TotalConIva);
    } else if (Pagos.length > 0) {
      useStorePos.getState().sincronizarTotalPagos(resumen.TotalConIva);
    }
  }, [Pagos.length, TicketLineas.length, modoRapido, resumen.TotalConIva]);

  useEffect(() => {
    if (!modoRapido || !cobroAbierto) return undefined;
    const cerrarConEscape = (event) => {
      if (event.key === "Escape" && !ConfirmandoVenta) setCobroAbierto(false);
    };
    window.addEventListener("keydown", cerrarConEscape);
    return () => window.removeEventListener("keydown", cerrarConEscape);
  }, [ConfirmandoVenta, cobroAbierto, modoRapido]);

  const abrirCobro = () => {
    if (TicketLineas.length === 0) return;
    useStorePos.getState().prepararCobro(resumen.TotalConIva);
    setCobroAbierto(true);
    window.setTimeout(() => document.querySelector(".pos-cierre-venta-rapida .pos-pago-fila select")?.focus(), 100);
  };

  const confirmarVenta = async () => {
    if (!puedeConfirmar) {
      if (!validacionPagos.valido || ErrorFormasPago) {
        document.querySelector(".pos-cierre-venta-rapida .pos-pago-fila select, .pos-cierre-venta-rapida .pos-pago-fila input")?.focus();
      } else if (!NumeradorId || ErrorOpcionesFacturacion) {
        document.querySelector(".pos-cierre-venta-rapida .pos-pago-fiscal-oculto select")?.focus();
      } else if (!formaFiscalValida) {
        document.querySelector(".pos-cierre-venta-rapida .pos-forma-fiscal-principal select")?.focus();
      } else if (ModoCliente === "consumidorFinal" && RequiereIdentificacionFiscal) {
        setCobroAbierto(false);
        window.setTimeout(() => document.querySelector(".pos-datos-rapidos .pos-btn-datos")?.click(), 80);
      }
      return;
    }
    const confirmada = await useStorePos.getState().confirmarVenta();
    if (!confirmada || !modoRapido) return;
    setCobroAbierto(false);
    window.setTimeout(() => document.querySelector(".pos-mobile-sale-flow .pos-producto-selector input")?.focus(), 100);
  };

  const confirmarEliminarLinea = (linea) => {
    Mensaje({
      title: t("pos.deleteLineQuestion"),
      text: getLineaNombre(linea, t),
      icon: demo,
      buttons: [t("common.cancel"), t("common.accept")],
      dangerMode: true,
    }).then((ok) => {
      if (!ok) return;
      useStorePos.getState().eliminarLinea(linea.Id);
    });
  };

  return (
    <div className="pos-ticket-shell">
      <div className="pos-ticket-header">
        <div className="pos-ticket-heading">
          <span className="pos-section-icon pos-section-icon-ticket">
            <i className="pi pi-receipt" aria-hidden="true" />
          </span>
          <div>
            <h2 className="ing-subtitulo">{t("pos.saleDetails")}</h2>
            <p className="pos-ayuda">
              {Cliente?.sDescripcion ? t("pos.customerLabel", { name: Cliente.sDescripcion }) : t("pos.finalConsumer")}
            </p>
          </div>
        </div>
        <div className="pos-ticket-badge">
          {t("pos.productCount", { count: TicketLineas.length })}
        </div>
      </div>

      <div className="pos-ticket-table-wrap">
        {TicketLineas.length === 0 ? (
          <div className="pos-ticket-empty">
            <i className="pi pi-receipt" aria-hidden="true" />
            <strong>{t("pos.noProductsYet")}</strong>
            <span>{t("pos.useAddProduct")}</span>
          </div>
        ) : modoRapido ? (
          <div className="pos-ticket-quick-list">
            {TicketLineas.map((linea) => (
              <article className="pos-ticket-quick-item" key={linea.Id}>
                <div className="pos-ticket-quick-main">
                  <span className="pos-ticket-quick-qty">{formatQty(linea.Cantidad, locale)}×</span>
                  <div className="pos-ticket-producto">
                    <strong>{getLineaNombre(linea, t)}</strong>
                    <span>
                      {linea.ProductoNuevo || linea.TipoLinea === "Libre" ? t("pos.newCode") : linea.Mercaderia?.sCodigo || t("pos.withoutCode")}
                      {getLineaUnidad(linea) ? ` · ${getLineaUnidad(linea)}` : ""}
                    </span>
                    <small className={`pos-linea-stock ${String(linea.EstadoStockEstimado || "NoAplica").toLowerCase()}`}>
                      {getStockEstimado(linea, t, locale)}
                    </small>
                    {linea.IvaPendiente && <small className="pos-linea-iva-pendiente">{t("pos.pendingVatFinalPrice")}</small>}
                    {linea.CategoriaFiscal && <small className="pos-linea-categoria-fiscal">{linea.CategoriaFiscal.Descripcion}</small>}
                    {(ErroresLineas[linea.Id] || []).map((error) => (
                      <small className="pos-linea-error" key={error}>{error}</small>
                    ))}
                  </div>
                </div>
                <div className="pos-ticket-quick-price">
                  <strong>{formatearMoneda(linea.Subtotal)}</strong>
                  <span>{formatearMoneda(linea.PrecioUnitario)} {t("pos.each")}</span>
                </div>
                <button
                  type="button"
                  className="pos-icon-btn danger"
                  onClick={() => confirmarEliminarLinea(linea)}
                  title={t("pos.deleteLine")}
                  aria-label={t("pos.deleteProduct", { product: getLineaNombre(linea, t) })}
                >
                  <i className="pi pi-trash" aria-hidden="true" />
                </button>
              </article>
            ))}
          </div>
        ) : (
          <table className="pos-ticket-table">
            <thead>
              <tr>
                <th>{t("common.code")}</th>
                <th>{t("common.description")}</th>
                <th className="num">{t("pos.quantityAbbreviation")}</th>
                <th className="num">{t("pos.unitPriceAbbreviation")}</th>
                <th className="num">{t("pos.discountAbbreviation")}</th>
                <th className="num">{t("pos.tax")}</th>
                <th className="num">{t("common.subtotal")}</th>
                <th aria-label={t("common.actions")}></th>
              </tr>
            </thead>
            <tbody>
              {TicketLineas.map((linea) => (
                <tr key={linea.Id}>
                  <td className="pos-ticket-codigo">
                    {linea.ProductoNuevo || linea.TipoLinea === "Libre" ? t("pos.newCode") : linea.Mercaderia?.sCodigo || "—"}
                  </td>
                  <td>
                    <div className="pos-ticket-producto">
                      <strong>{getLineaNombre(linea, t)}</strong>
                      {getLineaUnidad(linea) && <span>{t("common.unit")}: {getLineaUnidad(linea)}</span>}
                      <small className={`pos-linea-stock ${String(linea.EstadoStockEstimado || "NoAplica").toLowerCase()}`}>
                        {getStockEstimado(linea, t, locale)}
                      </small>
                      {linea.IvaPendiente && <small className="pos-linea-iva-pendiente">{t("pos.pendingVatFinalPrice")}</small>}
                      {linea.CategoriaFiscal && <small className="pos-linea-categoria-fiscal">{t("pos.taxCategoryLabel", { category: linea.CategoriaFiscal.Descripcion })}</small>}
                      {(ErroresLineas[linea.Id] || []).map((error) => (
                        <small className="pos-linea-error" key={error}>{error}</small>
                      ))}
                    </div>
                  </td>
                  <td className="num">{formatQty(linea.Cantidad, locale)}</td>
                  <td className="num">{formatearMoneda(linea.PrecioUnitario)}</td>
                  <td className="num">{formatearMoneda(linea.DescuentoLinea)}</td>
                  <td className="num">
                    {linea.IvaPendiente ? t("pos.pendingValue") : linea.IVAConocido === false ? "—" : `${Number(linea.PorcentajeIVA) || 0}%`}
                  </td>
                  <td className="num subtotal">{formatearMoneda(linea.Subtotal)}</td>
                  <td className="pos-ticket-actions">
                    <button
                      type="button"
                      className="pos-icon-btn danger"
                      onClick={() => confirmarEliminarLinea(linea)}
                      title={t("pos.deleteLine")}
                    >
                      <i className="pi pi-trash" aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modoRapido && (
        <div className="pos-resumen-grid pos-resumen-rapido-inline" aria-label={t("pos.saleSummary")}>
          <div className="pos-resumen-card">
            <span>{t("common.subtotal")}</span>
            <strong>{formatearMoneda(resumen.SubtotalBruto)}</strong>
          </div>
          <div className="pos-resumen-card">
            <span>{t("pos.discount")}</span>
            <strong>-{formatearMoneda(resumen.DescuentoTotal)}</strong>
          </div>
          <div className="pos-resumen-card">
            <span>{t("pos.tax")}</span>
            <strong>{formatearMoneda(resumen.IvaTotal)}</strong>
          </div>
          <div className="pos-resumen-card pos-resumen-total">
            <span>{t("common.total")}</span>
            <strong>{formatearMoneda(resumen.TotalConIva)}</strong>
          </div>
        </div>
      )}

      {modoRapido && (
        <div className="pos-mobile-cartbar" aria-label={t("pos.totalAndCheckout")}>
          <div className="pos-mobile-cartbar-total">
            <span>{t("pos.productCount", { count: TicketLineas.length })}</span>
            <strong>{formatearMoneda(resumen.TotalConIva)}</strong>
          </div>
          <button type="button" disabled={TicketLineas.length === 0} onClick={abrirCobro}>
            <i className="pi pi-wallet" aria-hidden="true" />
            <span>{t("pos.charge")}</span>
          </button>
        </div>
      )}

      <div
        className={`pos-cierre-venta ${modoRapido ? "pos-cierre-venta-rapida" : ""} ${cobroAbierto ? "abierto" : ""}`}
        role={modoRapido ? "dialog" : undefined}
        aria-modal={modoRapido ? "true" : undefined}
        aria-label={modoRapido ? t("pos.saleCheckout") : undefined}
      >
        {modoRapido && (
          <div className="pos-cobro-sheet-header">
            <div>
              <span className="pos-step">{t("pos.finishSale")}</span>
              <h2>{t("pos.chargeAmount", { amount: formatearMoneda(resumen.TotalConIva) })}</h2>
            </div>
            <button type="button" onClick={() => setCobroAbierto(false)} disabled={ConfirmandoVenta} aria-label={t("pos.closeCheckout")}>
              <i className="pi pi-times" aria-hidden="true" />
            </button>
          </div>
        )}
        <div className="pos-resumen-grid">
        <div className="pos-resumen-card">
          <span>{t("pos.grossSubtotal")}</span>
          <strong>{formatearMoneda(resumen.SubtotalBruto)}</strong>
        </div>
        <div className="pos-resumen-card">
          <span>{t("pos.discount")}</span>
          <strong>-{formatearMoneda(resumen.DescuentoTotal)}</strong>
        </div>
        <div className="pos-resumen-card">
          <span>{t("pos.tax")} {resumen.EsEstimado ? t("pos.estimated") : ""}</span>
          <strong>{formatearMoneda(resumen.IvaTotal)}</strong>
        </div>
        <div className="pos-resumen-card pos-resumen-total">
          <span>{t("common.total")}</span>
          <strong>{formatearMoneda(resumen.TotalConIva)}</strong>
        </div>
        </div>

        <div className="pos-pago-panel">
        <div className="pos-pago-heading">
          <div>
            <span className="pos-step">{t("pos.saleClosing")}</span>
            <h3>{t("pos.paymentAndConfirmation")}</h3>
          </div>
        </div>
        <div className="pos-facturacion-automatica" aria-label={t("pos.automaticInvoicing")}>
          <span className="pos-facturacion-icono"><i className="pi pi-verified" aria-hidden="true" /></span>
          <div>
            <strong>{t("pos.automaticElectronicInvoice")}</strong>
            <span>{t("pos.issuedOnConfirmation")}</span>
          </div>
        </div>
        <div className="pos-campo pos-campo-ancho pos-pago-fiscal-oculto">
          <div className="camp-o"><label>{t("pos.pointOfSaleAndNumbering")}</label></div>
          <select
            value={NumeradorId || ""}
            disabled={CargandoOpcionesFacturacion || OpcionesFacturacion.length === 0}
            onChange={(e) => useStorePos.getState().seleccionarNumerador(e.target.value)}
          >
            <option value="">{CargandoOpcionesFacturacion ? t("common.loading") : t("pos.selectOption")}</option>
            {OpcionesFacturacion.map((opcion) => (
              <option key={opcion.NumeradorId} value={opcion.NumeradorId}>
                {opcion.Letra} - {opcion.Descripcion} - PV {opcion.PuntoVenta}
                {opcion.ProximoNumero ? ` (${t("pos.nextNumberInfo", { number: opcion.ProximoNumero })})` : ""}
              </option>
            ))}
          </select>
          {ErrorOpcionesFacturacion && <small className="pos-linea-error" role="alert">{ErrorOpcionesFacturacion}</small>}
          {!ErrorOpcionesFacturacion && CondicionIVASeleccionada && <small>{CondicionIVASeleccionada}</small>}
        </div>
        {permiteCompraComoConsumidorFinal && (
          <label className="pos-facturar-consumidor-final">
            <span className="pos-facturar-consumidor-final-control">
              <input
                type="checkbox"
                checked={FacturarComoConsumidorFinal}
                disabled={ConfirmandoVenta}
                onChange={(event) => useStorePos.getState()
                  .setFacturarComoConsumidorFinal(event.target.checked)}
              />
              <strong>{t("pos.invoiceAsFinalConsumer")}</strong>
            </span>
            <small>
              {t("pos.invoiceAsFinalConsumerHelp")}
            </small>
          </label>
        )}
        <PosPagosEditor total={resumen.TotalConIva} />

        <div className="pos-campo pos-campo-ancho">
          <div className="camp-o">
            <label>{t("cash.observation")}</label>
          </div>
          <textarea
            rows="2"
            value={Observacion}
            onChange={(e) => useStorePos.getState().setObservacion(e.target.value)}
            placeholder={t("pos.saleNotesPlaceholder")}
          />
        </div>
        </div>

        {ModoCliente === "consumidorFinal" && RequiereIdentificacionFiscal && (
          <div className="pos-identificacion-alerta" role="alert">
            <i className="pi pi-exclamation-triangle" aria-hidden="true" />
            <div>
              <strong>{t("pos.cannotInvoiceFinalConsumer")}</strong>
              <span>
                {SolicitaIdentificacionFiscal
                  ? t("pos.taxIdRequiresRealCustomer")
                  : t("pos.amountRequiresIdentifiedCustomer")}
              </span>
            </div>
          </div>
        )}

        {modoRapido && motivoBloqueo && !ConfirmandoVenta && (
          <div className="pos-cobro-requisito" role="status">
            <i className="pi pi-info-circle" aria-hidden="true" />
            <span>{motivoBloqueo}</span>
          </div>
        )}

        <div className="pos-ticket-totalbar">
        <div className="pos-total-labels">
          <span>{t("pos.totalToCharge")}</span>
          <span className="pos-total-numero">{formatearMoneda(resumen.TotalConIva)}</span>
        </div>
        <button
          className="pos-btn-confirmar"
          disabled={!puedeConfirmar || !puedeAcceder(PERMISOS.VENTAS_CREAR)}
          onClick={confirmarVenta}
          aria-busy={ConfirmandoVenta}
        >
          <i
            className={`pi ${ConfirmandoVenta ? "pi-spin pi-spinner" : "pi-check"}`}
            aria-hidden="true"
          />
          <span>{!puedeAcceder(PERMISOS.VENTAS_CREAR) ? t("pos.noCreatePermission") : ConfirmandoVenta ? t("pos.issuing") : puedeConfirmar ? t("common.finish") : t("pos.completeData")}</span>
        </button>
        <div className="pos-total-labels pos-vuelto-box">
          <span>{t("pos.change")}</span>
          <span className="pos-vuelto">{formatearMoneda(resumenPagos.Vuelto)}</span>
        </div>
        </div>
      </div>

      {(EstadoEnvioVenta === "error" || EstadoEnvioVenta === "incierto") && ErrorFiscal && (
        <div className={`pos-envio-alerta ${EstadoEnvioVenta}`} role="alert">
          <strong>{EstadoEnvioVenta === "incierto" ? t("pos.resultPendingConfirmation") : t("pos.saleNotConfirmed")}</strong>
          <span>{ErrorFiscal}</span>
        </div>
      )}

      {VentaPendiente && !ResultadoVenta && TicketLineas.length === 0 && (
        <div className="pos-envio-alerta incierto" role="alert">
          <strong>{t("pos.recoveredPendingSale")}</strong>
          <span>{t("pos.pendingSaleRecoveryHelp")}</span>
          <button type="button" className="pos-btn-alerta" disabled={ConfirmandoVenta || !puedeAcceder(PERMISOS.VENTAS_CREAR)} onClick={() => useStorePos.getState().reintentarVentaPendiente()}>
            <i className="pi pi-refresh" aria-hidden="true" /> {ConfirmandoVenta ? t("pos.retrying") : t("pos.recoverSale")}
          </button>
        </div>
      )}

    </div>
  );
};

export default PosTicket;
